import { Loader2, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { SessionProgress } from '@/types/api.types';

export interface ProcessingScreenProps {
  sessionId: string;
  progress: SessionProgress | null;
  isPolling: boolean;
  isReady: boolean;
  isFailed: boolean;
  onCancel: () => void;
  onRetry: () => void;
  onViewResults: () => void;
}

export function ProcessingScreen({
  sessionId,
  progress,
  isPolling,
  isReady,
  isFailed,
  onCancel,
  onRetry,
  onViewResults,
}: ProcessingScreenProps) {
  const progressPercent = progress?.progress_percent ?? 0;
  const processedCount = progress?.processed_questions ?? 0;
  const totalCount = progress?.total_questions ?? 0;
  const status = progress?.status ?? 'pending';
  const errorMessage = progress?.error_message;

  // Auto-redirect is handled by parent component via onViewResults

  const getStatusIcon = () => {
    if (isFailed) {
      return <XCircle className="h-12 w-12 text-destructive" />;
    }
    if (isReady) {
      return <CheckCircle className="h-12 w-12 text-tier-exact" />;
    }
    return <Loader2 className="h-12 w-12 text-primary animate-spin" />;
  };

  const getStatusTitle = () => {
    if (isFailed) return 'Processing Failed';
    if (isReady) return 'Processing Complete';
    if (status === 'pending') return 'Preparing...';
    return 'Processing Questions';
  };

  const getStatusDescription = () => {
    if (isFailed) {
      return errorMessage || 'An error occurred during processing. Please try again.';
    }
    if (isReady) {
      return `Successfully processed ${totalCount} questions. Click below to review the results.`;
    }
    if (status === 'pending') {
      return 'Your session is being prepared. This should only take a moment...';
    }
    return `Processing question ${processedCount} of ${totalCount}...`;
  };

  return (
    <Card className="max-w-lg mx-auto">
      <CardHeader className="text-center pb-2">
        <div className="flex justify-center mb-4">{getStatusIcon()}</div>
        <CardTitle className="text-xl">{getStatusTitle()}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress indicator */}
        {!isFailed && !isReady && (
          <div className="space-y-2">
            <Progress value={progressPercent} className="h-3" />
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{processedCount} of {totalCount} questions</span>
              <span>{Math.round(progressPercent)}%</span>
            </div>
          </div>
        )}

        {/* Status message */}
        <p className={cn(
          'text-center text-sm',
          isFailed ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {getStatusDescription()}
        </p>

        {/* Session ID for reference */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            Session ID: <code className="bg-muted px-1 py-0.5 rounded text-xs">{sessionId}</code>
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex justify-center gap-3">
          {isFailed && (
            <>
              <Button variant="outline" onClick={onCancel}>
                Start Over
              </Button>
              <Button onClick={onRetry}>
                Retry Processing
              </Button>
            </>
          )}

          {isReady && (
            <Button onClick={onViewResults} size="lg">
              <CheckCircle className="h-4 w-4 mr-2" />
              View Results
            </Button>
          )}

          {isPolling && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>

        {/* Note about processing */}
        {isPolling && (
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
            <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground">
              You can safely navigate away from this page. Your session will continue processing
              in the background and will be available in your sessions list.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
